import { useEffect, useRef, useState } from 'react';
import { selectDirty, useStore } from '../store';
import { useEditorState } from '../hooks/useEditorState';
import { useShareUrl } from '../hooks/useShareUrl';
import { displayUrl, saveShare, updateShare } from '../api';
import { DomainSwitcher } from './DomainSwitcher';
import { SharesMenu } from './SharesMenu';
import { MoreMenu } from './MoreMenu';
import { PayloadsMenu } from './PayloadsMenu';

type SaveResult = { kind: 'share'; replayUrl: string } | { kind: 'error'; message: string } | null;

export function TopBar() {
  const shareUrl = useShareUrl();
  const state = useEditorState();
  const copied = useStore((s) => s.copied);
  const setCopied = useStore((s) => s.setCopied);
  const domainBase = useStore((s) => s.domainBase);
  const domainSub = useStore((s) => s.domainSub);
  const currentShare = useStore((s) => s.currentShare);
  const previewOpen = useStore((s) => s.previewOpen);
  const payloadsOpen = useStore((s) => s.payloadsOpen);
  const setPayloadsOpen = useStore((s) => s.setPayloadsOpen);
  const dirty = useStore(selectDirty);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<SaveResult>(null);
  const [sharesOpen, setSharesOpen] = useState(false);
  const [resultCopied, setResultCopied] = useState(false);

  const copy = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };

  const copyResult = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setResultCopied(true);
      window.setTimeout(() => setResultCopied(false), 1500);
    });
  };

  const handleNew = () => {
    useStore.getState().newDocument();
    setResult(null);
  };

  // Save: PATCH the open share, or POST a new one and switch to it.
  const persist = async (forceNew: boolean) => {
    if (saving) return;
    setSaving(true);
    try {
      const open = useStore.getState().currentShare;
      const saved =
        open && !forceNew
          ? await updateShare(open.id, { state })
          : await saveShare(state, forceNew && open?.name ? `${open.name} (copy)` : undefined);
      useStore.getState().setCurrentShare({ id: saved.id, name: saved.name, updatedAt: saved.updatedAt });
      history.replaceState(null, '', `/e/${saved.id}`);
      setResult({ kind: 'share', replayUrl: displayUrl(`/s/${saved.id}`, { domainBase, domainSub }) });
      window.dispatchEvent(new Event('xsscool:shares-changed'));
    } catch (e) {
      setResult({ kind: 'error', message: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const persistRef = useRef(persist);
  persistRef.current = persist;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === 's') {
        e.preventDefault();
        void persistRef.current(e.shiftKey);
      } else if (k === 'k') {
        e.preventDefault();
        useStore.getState().setPaletteOpen(!useStore.getState().paletteOpen);
      }
    };
    const onSave = (e: Event) => void persistRef.current(!!(e as CustomEvent<{ forceNew?: boolean }>).detail?.forceNew);
    window.addEventListener('keydown', onKey);
    window.addEventListener('xsscool:save', onSave);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('xsscool:save', onSave);
    };
  }, []);

  // Unsaved edits on an open share: warn before the tab goes away.
  useEffect(() => {
    if (!dirty) return;
    const onUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty]);

  return (
    <header className="topbar">
      <a className="brand" href="/" title="Home">
        <span className="brand-icon" aria-hidden="true">😎</span>
        <span className="brand-name">xss.cool</span>
      </a>
      <DomainSwitcher />
      {currentShare && (
        <button
          type="button"
          className={'share-chip' + (dirty ? ' dirty' : '')}
          onClick={() => setSharesOpen(true)}
          title={dirty ? `Unsaved changes to share ${currentShare.id}. Press ⌘S to save.` : `Share ${currentShare.id} is saved.`}
        >
          <span className="share-chip-dot" aria-hidden="true" />
          {currentShare.name || currentShare.id}
        </button>
      )}
      <div className="spacer" />
      {result?.kind === 'share' && (
        <div className="save-result">
          <span className="save-result-label">Replay:</span>
          <input readOnly value={result.replayUrl} onFocus={(e) => e.currentTarget.select()} />
          <button onClick={() => copyResult(result.replayUrl)}>{resultCopied ? 'Copied' : 'Copy'}</button>
          <button onClick={() => setResult(null)} aria-label="Dismiss">×</button>
        </div>
      )}
      {result?.kind === 'error' && (
        <div className="save-result error">
          <span>Save failed: {result.message}</span>
          <button onClick={() => setResult(null)}>×</button>
        </div>
      )}
      <button onClick={handleNew} title="Clear the editor and start a new scratch document (undo from the toast)">
        New
      </button>
      <button
        onClick={() => void persist(false)}
        disabled={saving || (!!currentShare && !dirty)}
        className={currentShare && !dirty ? '' : 'primary'}
        title={
          currentShare
            ? dirty
              ? 'Update this share in place (⌘S). The replay URL stays the same.'
              : 'No changes since the last save. Edit something, or use Save as new to fork.'
            : 'Save as a share (⌘S): you get a replay URL and the editor URL moves to /e/<id>.'
        }
      >
        {saving ? 'Saving' : currentShare && !dirty ? 'Saved' : 'Save'}
      </button>
      {currentShare && (
        <button
          onClick={() => void persist(true)}
          disabled={saving}
          title="Fork: save the current state as a separate new share (⌘⇧S)"
        >
          Save as new
        </button>
      )}
      <div className="menu-wrap">
        <button
          onClick={() => setPayloadsOpen(!payloadsOpen)}
          title="Payload library: presets to start from (⌘B)"
          aria-haspopup="dialog"
          aria-expanded={payloadsOpen}
        >
          Payloads ▾
        </button>
        <PayloadsMenu />
      </div>
      <div className="menu-wrap">
        <button
          onClick={() => setSharesOpen((v) => !v)}
          title="Your saved shares"
          aria-haspopup="dialog"
          aria-expanded={sharesOpen}
        >
          Shares ▾
        </button>
        <SharesMenu open={sharesOpen} onClose={() => setSharesOpen(false)} mode="manage" />
      </div>
      <button onClick={() => copy(shareUrl)} title="Copy the live crafted URL to the clipboard">
        {copied ? 'Copied!' : 'Copy URL'}
      </button>
      <button onClick={() => window.open(shareUrl, '_blank', 'noopener')} title="Open the response in a new tab">
        Open
      </button>
      <button
        onClick={() => useStore.getState().setPreviewOpen(!previewOpen)}
        title="Toggle the preview iframe"
        aria-pressed={previewOpen}
      >
        Preview
      </button>
      <MoreMenu />
    </header>
  );
}
